import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UseInstallPromptState {
  canInstall: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<void>;
}

function getIsStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Window['navigator'] & { standalone?: boolean }).standalone === true
  );
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
let listening = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function ensureListening(): void {
  if (listening || typeof window === 'undefined') {
    return;
  }
  listening = true;
  installed = getIsStandalone();
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
}

export const useInstallPrompt = (): UseInstallPromptState => {
  const [, setVersion] = useState(0);

  useEffect(() => {
    ensureListening();
    installed = getIsStandalone();
    const unsubscribe = (): void => {
      listeners.delete(rerender);
    };
    const rerender = (): void => {
      setVersion((version) => version + 1);
    };
    listeners.add(rerender);
    rerender();
    return unsubscribe;
  }, []);

  const promptInstall = useCallback(async (): Promise<void> => {
    if (!deferredPrompt) {
      return;
    }
    const promptEvent = deferredPrompt;
    await promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === 'accepted') {
      deferredPrompt = null;
      installed = true;
      notify();
    }
  }, []);

  return {
    canInstall: deferredPrompt !== null && !installed,
    isInstalled: installed,
    promptInstall,
  };
};
